import { useEffect, useState } from 'react';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { apiError, useImagingApi } from './api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
export function ImageViewer({
  studyId,
  objects,
  onExtract,
}: {
  studyId: string;
  onExtract?: (objectId: string, frame: number) => Promise<void>;
  objects: { id: string; sop_uid: string; image_info?: { frames: number; rows: number; source_filename?: string; source_page?: number } }[];
}) {
  const [open, setOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [selected, setSelected] = useState('');
  const [frame, setFrame] = useState(0);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { base } = useImagingApi();
  const imageObjects = objects.filter((o) => o.image_info?.rows);
  const obj = imageObjects.find((o) => o.id === selected) || imageObjects[0];
  useEffect(() => {
    if (!open || !obj) return;
    let active = true;
    let blobUrl = '';
    const abort = new AbortController();
    setLoading(true);
    setError('');
    setUrl('');
    clientApi
      .get(`${base}/studies/${studyId}/objects/${obj.id}/frames/${frame}`, {
        responseType: 'blob',
        signal: abort.signal,
      })
      .then((result) => {
        blobUrl = URL.createObjectURL(result.data);
        if (active) setUrl(blobUrl);
      })
      .catch((e) => {
        if (active) setError(apiError(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      abort.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // `obj?.id`, not `obj`. Depending on the object identity would re-fetch the
    // frame on every render and revoke the blob URL under the open viewer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, obj?.id, frame, base, studyId]);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        Open images
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Study images · 2D frame review</DialogTitle>
          </DialogHeader>
          <p>
            Grayscale previews use per-frame display scaling. Original DICOM objects remain available for a full
            diagnostic viewer.
          </p>
          <div className="flex flex-wrap gap-2">
            {imageObjects.map((o, i) => (
              <Button
                key={o.id}
                variant="outline"
                onClick={() => {
                  setSelected(o.id);
                  setFrame(0);
                }}>
                {o.image_info?.source_filename ? `${o.image_info.source_filename} · page ${o.image_info.source_page}` : `Object ${i + 1}`}
              </Button>
            ))}
          </div>
          {!imageObjects.length && <p>No supported image objects are available.</p>}
          {loading && <p>Loading frame…</p>}
          {error && <p role="alert">{error}</p>}
          {url && <img src={url} alt={`DICOM frame ${frame + 1}`} className="max-h-[55vh] object-contain" />}
          {obj && onExtract && (
            <Button
              disabled={extracting || loading}
              onClick={async () => {
                setExtracting(true);
                try {
                  await onExtract(obj.id, frame);
                  setOpen(false);
                } finally {
                  setExtracting(false);
                }
              }}>
              {extracting ? 'Extracting…' : 'Extract data-page measurements for review'}
            </Button>
          )}
          {obj && (
            <div className="flex items-center gap-3">
              <Button variant="outline" disabled={frame === 0} onClick={() => setFrame((v) => v - 1)}>
                Previous
              </Button>
              <label>
                Frame{' '}
                <Input
                  min={1}
                  type="number"
                  value={frame + 1}
                  aria-label="Frame number"
                  max={obj.image_info?.frames || 1}
                  onChange={(e) =>
                    setFrame(Math.max(0, Math.min((obj.image_info?.frames || 1) - 1, Number(e.target.value) - 1)))
                  }
                />
              </label>
              <span>of {obj.image_info?.frames || 1}</span>
              <Button
                variant="outline"
                disabled={frame + 1 >= (obj.image_info?.frames || 1)}
                onClick={() => setFrame((v) => v + 1)}>
                Next
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
